import React, { memo, useState, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import isEqual from 'lodash/isEqual'
import { Button } from 'transfix-ui/components/Button'
import { InlineMessage } from 'transfix-ui/components/InlineMessage'
import { useRefresh } from 'utils/hooks/useRefresh'
import { TruckAutoSuggest } from 'apps/fleet-planner/components/atoms'
import { TruckForm } from 'apps/fleet-planner/components/forms/TruckForm'
import { AddEntityModal } from 'apps/fleet-planner/components/modals/AddEntityModal'
import { FP_SUPPORT_EMAIL } from 'apps/fleet-planner/config'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useTruckApi } from 'apps/fleet-planner/hooks/useTruckApi'
import { useTruckInputState } from 'apps/fleet-planner/hooks/useTruckInputState'
import { INewTruck } from 'apps/fleet-planner/schema'
import * as Types from 'apps/fleet-planner/types'
import { fetchEquipmentSuccess } from 'apps/fleet-planner/utils/actions/equipment'
import * as Api from 'apps/fleet-planner/utils/api'

const EMPTY_OBJECT = {}

export interface ITruckProps {
  value: Partial<Types.ILoad>
  refetchOptions: boolean
  error?: string
  onChange: (change: Partial<Types.ILoad>) => void
  onValidationChange?: (message?: string) => void
  onBlur?: () => void
  /**
   * @default true
   */
  large?: boolean
  selectedMotorCarrierId?: number
}

const getDefaultTruckState = (
  motorCarrierId?: number,
  truckNumber: string = ''
): Partial<Types.ITruck> => ({
  motorCarrierId,
  truckNumber,
})

export const Truck = memo<ITruckProps>(
  ({
    error: errorProp,
    large: largeProp = true,
    onBlur: onBlurProp,
    onChange,
    onValidationChange: onValidationChangeProp,
    selectedMotorCarrierId,
    value: valueProp,
  }) => {
    const { trackEvent } = useFMSAnalytics()
    const { truck: existingTruck = {} as Partial<Types.ITruck> } = valueProp || {}

    const dispatch = useDispatch()

    const { createNewTruck, error: apiError, isLoading: isApiLoading, resetError } = useTruckApi()

    const { getErrorMessage, onBlurTrigger, onChangeTrigger, resetState } = useTruckInputState()

    const [inputValue, setInputValue] = useState<string | undefined>('')

    const [isTruckFormVisible, setTruckFormVisible] = useState<boolean>(false)
    const [currentTruck, setCurrentTruck] = useState<Partial<Types.ITruck>>(
      existingTruck ?? EMPTY_OBJECT
    )
    const [defaultValues, setDefaultValues] = useState<Partial<Types.ITruck>>(
      getDefaultTruckState(valueProp?.motorCarrierId)
    )

    const { key: autocompleteKey, resetKey } = useRefresh(currentTruck)

    // When value updates, also update local state
    useEffect(() => {
      setDefaultValues(getDefaultTruckState(valueProp.motorCarrierId))
    }, [valueProp.motorCarrierId])

    // Update truck props
    useEffect(() => {
      if (!isEqual(existingTruck, currentTruck)) {
        setCurrentTruck(existingTruck ?? EMPTY_OBJECT)
      }
    }, [currentTruck, existingTruck, resetKey])

    const truckChangeHandler = useCallback(
      (selectedTruck?: Partial<Types.ITruck> | undefined) => {
        onChange({ truck: selectedTruck as Types.ITruck })
        resetState()
        if (!selectedTruck?.id) setInputValue('')
      },
      [onChange, resetState]
    )

    const cancelAddNewTruckHandler = () => {
      setTruckFormVisible(false)
      setDefaultValues(prev => ({
        ...prev,
        truckNumber: '',
      }))
      resetKey()
      resetError()
      setInputValue('')
    }

    const addEntityHandler = (val?: string) => {
      setDefaultValues(prev => ({
        ...prev,
        truckNumber: val || '',
      }))
      setTruckFormVisible(true)
      setInputValue(val)
    }

    const addEntityClickHandler = () => setTruckFormVisible(true)

    /**
     * Create a new driver using form input
     */
    const addNewTruckHandler = useCallback(
      async (t: INewTruck) => {
        resetError()
        const truck = await createNewTruck(t)

        if (truck) {
          const equipment = await Api.fetchEquipment()
          truckChangeHandler(truck)
          dispatch(fetchEquipmentSuccess(equipment))
          setTruckFormVisible(false)
          resetKey()

          // Used only for validation
          setInputValue(truck.truckNumber)
        }
      },
      [createNewTruck, dispatch, resetError, resetKey, truckChangeHandler]
    )

    const inputChangeHandler = (val: string = '') => {
      setInputValue(val)
      onChangeTrigger()
    }

    const error = getErrorMessage({
      value: valueProp.truck,
      currentTruck,
      inputValue,
    })

    // Send latest validation error to parent component
    useEffect(() => {
      if (onValidationChangeProp) onValidationChangeProp(error)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error])

    const trucksBulkUploadClickHandler = () =>
      trackEvent('Bulk Upload', 'CLICK_TRUCKS_BULK_UPLOAD_LINK')

    const blurHandler = useCallback(() => {
      onBlurTrigger()
      if (onBlurProp) onBlurProp()
    }, [onBlurProp, onBlurTrigger])

    return (
      <div className='load-truck-wrapper' data-test='load-truck'>
        <div>
          <TruckAutoSuggest
            key={autocompleteKey}
            value={currentTruck}
            onChange={truckChangeHandler}
            onAddEntity={addEntityHandler}
            onCancel={cancelAddNewTruckHandler}
            disabled={isTruckFormVisible}
            onInputChange={inputChangeHandler}
            error={errorProp || error}
            fetchList={false}
            onBlur={blurHandler}
            large={largeProp}
            selectedMotorCarrierId={selectedMotorCarrierId}
          />
          {!currentTruck.id && (
            <Button
              className='btn-text-link-b mt1'
              data-test='add-new-truck-cta'
              onClick={addEntityClickHandler}>
              + Add new truck
            </Button>
          )}
          {isTruckFormVisible && (
            <AddEntityModal
              open
              title='Add New Truck'
              error={apiError}
              onClose={cancelAddNewTruckHandler}>
              <InlineMessage
                className='mb5 mt3 mt0-m'
                message={
                  <span>
                    <b>Lots of trucks to add?</b>{' '}
                    <a href={`mailto:${FP_SUPPORT_EMAIL}`} onClick={trucksBulkUploadClickHandler}>
                      Contact us
                    </a>{' '}
                    and we’ll import your trucks in bulk.
                  </span>
                }
              />
              <TruckForm
                defaultValues={{
                  ...defaultValues,
                  motorCarrierId: selectedMotorCarrierId,
                }}
                onSuccess={addNewTruckHandler}
                onCancel={cancelAddNewTruckHandler}
                isLoading={isApiLoading}
                disabledFields={selectedMotorCarrierId ? ['motorCarrierId'] : []}
              />
            </AddEntityModal>
          )}
        </div>
      </div>
    )
  }
)
